import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  Divider,
  Grid,
  Chip,
  Autocomplete,
} from '@mui/material';
import {
  Person as PersonIcon,
  Save as SaveIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { filterInput } from '../../utils/inputSanitization';
import { useDirectorySearch } from '../../hooks/useDirectorySearch';

const UserForm = ({ groups, onSubmit, onCancel }) => {
  const [groupSearch, setGroupSearch] = useState('');
  const [userForm, setUserForm] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    enabled: true,
    groups: [],
    attributes: {}
  });

  // Typing searches Keycloak so any group is reachable, not just the page the
  // parent preloaded. Selected names stay pinned so their chips remain valid
  // when the option list is replaced by search results.
  const groupResults = useDirectorySearch('groups', groupSearch);
  const groupOptions = useMemo(() => {
    const source = groupSearch.trim() ? groupResults.results : (groups || []);
    const names = source.map((g) => g.name);
    return [...names, ...userForm.groups.filter((name) => !names.includes(name))];
  }, [groups, groupSearch, groupResults.results, userForm.groups]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success = await onSubmit(userForm);
    if (success) {
      setUserForm({
        username: '',
        email: '',
        firstName: '',
        lastName: '',
        enabled: true,
        groups: [],
        attributes: {}
      });
    }
  };

  const handleGroupChange = (event, newValue) => {
    setUserForm({
      ...userForm,
      groups: newValue,
    });
  };

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#FFFFFF',
      borderRadius: '6px',
      fontFamily: 'Metropolis, sans-serif',
      '& fieldset': {
        borderColor: '#E5E5E5',
      },
      '&:hover fieldset': {
        borderColor: '#4698D3',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#4698D3',
      },
    },
    '& .MuiInputLabel-root': {
      fontFamily: 'Metropolis, sans-serif',
      color: '#666666',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#4698D3',
    },
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          p: 3,
          borderBottom: '1px solid #E5E5E5',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              backgroundColor: '#4698D3',
              width: 48,
              height: 48,
            }}
          >
            <PersonIcon sx={{ fontSize: 24 }} />
          </Avatar>
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'Bitter, Georgia, serif',
                fontWeight: 600,
                fontSize: '1.5rem',
                color: '#253E66',
              }}
            >
              Create User
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#999999',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 500,
              }}
            >
              Add a new user to the system
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Form Content */}
      <Box sx={{ p: 3 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Username"
                value={userForm.username}
                onChange={(e) => setUserForm({ ...userForm, username: filterInput(e.target.value, 'USERNAME') })}
                required
                size="small"
                sx={inputStyles}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: filterInput(e.target.value, 'EMAIL') })}
                required
                size="small"
                sx={inputStyles}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                value={userForm.firstName}
                onChange={(e) => setUserForm({ ...userForm, firstName: filterInput(e.target.value, 'GENERAL_TEXT') })}
                required
                size="small"
                sx={inputStyles}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={userForm.lastName}
                onChange={(e) => setUserForm({ ...userForm, lastName: filterInput(e.target.value, 'GENERAL_TEXT') })}
                required
                size="small"
                sx={inputStyles}
              />
            </Grid>

            {/* Group selection */}
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  color: '#253E66',
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                }}
              >
                Assign User to Groups
              </Typography>
              <Autocomplete
                multiple
                options={groupOptions}
                value={userForm.groups}
                onChange={handleGroupChange}
                disableCloseOnSelect
                inputValue={groupSearch}
                onInputChange={(_, value, reason) => {
                  // Ignore MUI's internal 'reset' events (fired on select/blur),
                  // which would otherwise wipe the active query.
                  if (reason === 'input' || reason === 'clear') setGroupSearch(value);
                }}
                loading={groupResults.loading}
                loadingText="Searching groups..."
                // Matching is done by Keycloak via the `search` parameter.
                filterOptions={(options) => options}
                noOptionsText={groupSearch ? 'No groups match this search' : 'No groups available'}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(70, 152, 211, 0.1)',
                        border: '1px solid rgba(70, 152, 211, 0.2)',
                        color: '#4698D3',
                        borderRadius: '4px',
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        '& .MuiChip-deleteIcon': {
                          color: '#4698D3',
                          '&:hover': { color: '#3580B8' },
                        },
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder={userForm.groups.length === 0 ? "Search groups..." : ""}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <SearchIcon sx={{ color: '#999999', fontSize: 18, ml: 0.5, mr: 0.5, flexShrink: 0 }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                    sx={inputStyles}
                  />
                )}
                slotProps={{
                  paper: {
                    sx: {
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E5E5E5',
                      borderRadius: '6px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                      mt: 0.5,
                      '& .MuiAutocomplete-option': {
                        fontFamily: 'Metropolis, sans-serif',
                        fontSize: '0.875rem',
                        '&:hover': {
                          backgroundColor: '#F9FBFD',
                        },
                        '&[aria-selected="true"]': {
                          backgroundColor: 'rgba(70, 152, 211, 0.1)',
                          color: '#4698D3',
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor: 'rgba(70, 152, 211, 0.15)',
                          },
                        },
                      },
                      '& .MuiAutocomplete-noOptions': {
                        fontFamily: 'Metropolis, sans-serif',
                        color: '#999999',
                        fontStyle: 'italic',
                      },
                    },
                  },
                }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3, borderColor: '#E5E5E5' }} />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={onCancel}
              sx={{
                color: '#666666',
                borderColor: '#E5E5E5',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                px: 3,
                borderRadius: '6px',
                '&:hover': {
                  borderColor: '#BDBDBD',
                  backgroundColor: '#F9FBFD',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              sx={{
                backgroundColor: '#4698D3',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                px: 3,
                borderRadius: '6px',
                '&:hover': {
                  backgroundColor: '#3580B8',
                },
              }}
            >
              Create User
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default UserForm;
